import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { fetchReviews } from '../services/reviewService';
import { Review } from '../types';

const formatReviewDate = (dateIso?: string, dateRelative?: string): string => {
  if (dateRelative) return dateRelative;
  if (dateIso) {
    try {
      return new Date(dateIso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateIso;
    }
  }
  return '';
};

const StarRating: React.FC<{ rating: number }> = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} star rating`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.round(rating) ? 'text-primary-gold fill-primary-gold' : 'text-neutral-medium-gray'}
      />
    ))}
  </div>
);

const ImageLightbox: React.FC<{
  images: string[];
  startIndex: number;
  onClose: () => void;
}> = ({ images, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);

  const prev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const next = useCallback(() => {
    setIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, prev, next]);

  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20 transition"
        onClick={onClose}
      >
        <X size={28} />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            className="absolute left-4 text-white p-2 rounded-full hover:bg-white/20 transition"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            <ChevronLeft size={32} />
          </button>
          <button
            type="button"
            aria-label="Next image"
            className="absolute right-4 text-white p-2 rounded-full hover:bg-white/20 transition"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}

      <img
        src={images[index]}
        alt={`Review ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  );
};

const ReviewCard: React.FC<{
  review: Review;
  index: number;
  onImageClick: (images: string[], startIndex: number) => void;
}> = ({ review, index, onImageClick }) => {
  const dateLabel = formatReviewDate(review.dateIso, review.dateRelative);
  const images = review.reviewImages || [];

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
      className="card card-luxury"
    >
      <div className="card-content space-y-4">
        <div className="flex items-start gap-4">
          {review.authorPhotoUrl ? (
            <img
              src={review.authorPhotoUrl}
              alt={review.author}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-neutral-medium-gray"
              loading="lazy"
            />
          ) : (
            <div className="w-12 h-12 rounded-full flex-shrink-0 bg-primary-deep-blue/10 text-primary-deep-blue flex items-center justify-center font-semibold font-[var(--font-secondary)]">
              {(review.author || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h3 className="font-semibold text-neutral-charcoal font-[var(--font-secondary)] truncate">
                {review.author}
              </h3>
              <StarRating rating={review.rating} />
            </div>
            {dateLabel && (
              <p className="body-small text-secondary-slate mt-1">{dateLabel}</p>
            )}
          </div>
        </div>

        {review.text && (
          <p className="body-regular text-neutral-dark-gray whitespace-pre-line">
            {review.text}
          </p>
        )}

        {images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((src, i) => (
              <button
                key={`${src}-${i}`}
                type="button"
                className="relative aspect-square rounded-lg overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary-gold"
                onClick={() => onImageClick(images, i)}
              >
                <img
                  src={src}
                  alt={`By ${review.author} ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}

        {review.ownerResponse?.text && (
          <div className="mt-2 pl-4 border-l-4 border-primary-gold bg-secondary-cream/60 dark:bg-secondary-pearl rounded-r-xl p-4">
            <div className="flex items-center gap-2 mb-2 text-primary-deep-blue">
              <MessageSquare size={16} />
              <span className="body-small font-semibold">Response from RAISE Tours</span>
            </div>
            <p className="body-small text-neutral-dark-gray whitespace-pre-line">
              {review.ownerResponse.text}
            </p>
            {(review.ownerResponse.dateRelative || review.ownerResponse.dateIso) && (
              <p className="body-small text-secondary-slate mt-2">
                {formatReviewDate(
                  review.ownerResponse.dateIso,
                  review.ownerResponse.dateRelative
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.article>
  );
};

const ReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const items = await fetchReviews();
        setReviews(items);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen"
    >
      <section className="bg-neutral-charcoal pt-28 pb-16">
        <div className="container text-center">
          <h1 className="heading-primary text-neutral-white">Traveler Reviews</h1>
          <p className="body-large text-neutral-white/90 mt-4 max-w-2xl mx-auto">
            Authentic experiences shared by our guests on Google
          </p>
        </div>
      </section>

      <section className="bg-neutral-white py-16">
        <div className="container max-w-3xl">
          {loading && (
            <div className="text-center py-16">
              <div className="inline-block w-10 h-10 border-4 border-primary-deep-blue border-t-transparent rounded-full animate-spin" />
              <p className="body-regular text-secondary-slate mt-4">Loading reviews…</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-16">
              <p className="body-large text-error">{error}</p>
            </div>
          )}

          {!loading && !error && reviews.length === 0 && (
            <div className="text-center py-16">
              <p className="body-large text-secondary-slate">
                Reviews will appear here once they have been synced from Google.
              </p>
            </div>
          )}

          {!loading && !error && reviews.length > 0 && (
            <div className="space-y-6">
              {reviews.map((review, index) => (
                <ReviewCard
                  key={review.reviewId || review.id}
                  review={review}
                  index={index}
                  onImageClick={(images, startIndex) =>
                    setLightbox({ images, index: startIndex })
                  }
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <ImageLightbox
            images={lightbox.images}
            startIndex={lightbox.index}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReviewsPage;
